<template>
  <div ref="pageRef" class="juou-card menu-manage-page">
    <HpsfirProTable
      ref="tableRef"
      hide-export
      title="业务菜单管理"
      :columns="columns"
      :get-data="getData"
      :on-add="addFun"
      :on-delete="deleteFun"
      :on-edit="updateFun"
      :is-dangder-delete="true"
      :format-dangder-delete-info="formatDeleteInfo"
      add-text="新建目录"
      :add-init-values="addInitValues"
      :loading="loading"
      :default-expand-all="true"
      row-key="id"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import { ElMessage } from 'element-plus';
import {
  useBaseAsync,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
} from 'hpsfir-managebase-ui';
import { BASEMENUTYPEE, type BaseMenuAdminVo } from '@/api';
import { useApi } from '@/hooks/useApi';
import { normalizeBizTableColumns } from '@/utils/tableColumns';
import type { BusinessMenuDirectoryRequest, BusinessMenuMoveRequest } from '@/api/businessMenu';

type MenuRow = BaseMenuAdminVo & {
  children?: MenuRow[];
  parent?: MenuRow;
  hideOp?: Record<string, boolean>;
};

type DropMode = BusinessMenuMoveRequest['mode'];

const pageRef = ref<HTMLElement>();
const tableRef = ref<any>();
const loading = ref(false);
const currentRows = ref<MenuRow[]>([]);
const addInitValues = ref<Record<string, unknown>>({
  type: BASEMENUTYPEE.Dir,
  enable: true,
  sequence: 10,
  icon: 'Folder',
});
const draggingMenuId = ref<number>();
let mutationObserver: MutationObserver | undefined;
let bindTimer: number | undefined;

const getMenuMap = () => new Map(currentRows.value.map((row) => [row.id, row]));

const renderTitle = ({ row }: { row: MenuRow }) => h('span', {
  'class': 'menu-title-text',
  'data-menu-id': String(row.id),
}, row.title);

const adjustMenuColumnWidth = (column: HpsfirProTableColumn) => {
  if (column.name === 'title') {
    return { ...column, width: 240 };
  }
  if (column.name === 'url') {
    return { ...column, width: 180 };
  }
  return column;
};

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([
  {
    name: 'title', label: '标题', type: 'string', isCondition: true,
    rule: [{ required: true }], render: renderTitle,
  },
  {
    name: 'type', label: '类型', type: 'searchSelect', options: [
      { value: BASEMENUTYPEE.Link, label: '菜单' },
      { value: BASEMENUTYPEE.Dir, label: '目录' },
    ],
    clearable: false,
    hideEdit: true,
    hideCreate: true,
    width: 96,
  },
  {
    name: 'sequence', label: '序号', type: 'number',
    rule: [{ required: true }],
    width: 96,
  },
  {
    name: 'url', label: '地址', type: 'string', hideEdit: true, hideCreate: true,
  },
  {
    name: 'icon', label: '图标', type: 'string', width: 132,
  },
]).map(adjustMenuColumnWidth));

const cleanConditions = (conditions: Record<string, any>) => Object.entries(conditions || {}).reduce((res, [key, val]) => {
  if (val === null || val === undefined || val === '') {
    return res;
  }
  res[key] = val;
  return res;
}, {} as Record<string, any>);

const toTree = (menus: MenuRow[], parentId?: number, parent?: MenuRow): MenuRow[] => menus
  .filter((item) => item.parentId === parentId)
  .map((item) => {
    const row: MenuRow = {
      ...item,
      parent,
      hideOp: {
        delete: item.type !== BASEMENUTYPEE.Dir,
      },
    };
    const children = toTree(menus, row.id, row);
    row.children = children.length ? children : undefined;
    return row;
  });

const flattenRows = (rows: MenuRow[]): MenuRow[] => rows.flatMap((row) => [
  row,
  ...flattenRows(row.children || []),
]);

const getNextRootSequence = () => {
  const maxSequence = currentRows.value
    .filter((row) => !row.parentId)
    .reduce((max, row) => Math.max(max, row.sequence || 0), 0);
  return maxSequence + 10;
};

const refreshAddInitValues = () => {
  addInitValues.value = {
    type: BASEMENUTYPEE.Dir,
    enable: true,
    sequence: getNextRootSequence(),
    icon: 'Folder',
  };
};

const clearDropState = () => {
  pageRef.value?.querySelectorAll('.menu-drag-over-before, .menu-drag-over-after, .menu-drag-over-child, .menu-dragging')
    .forEach((item) => item.classList.remove(
      'menu-drag-over-before',
      'menu-drag-over-after',
      'menu-drag-over-child',
      'menu-dragging',
    ));
};

const getDragRow = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return undefined;
  }
  return target.closest<HTMLElement>('.menu-drag-row');
};

const getRowMenuId = (rowEl?: HTMLElement) => Number(rowEl?.dataset.menuId || 0);

const getDropMode = (rowEl: HTMLElement, targetMenu: MenuRow, event: DragEvent): DropMode => {
  const rect = rowEl.getBoundingClientRect();
  const ratio = (event.clientY - rect.top) / rect.height;
  if (ratio < 0.25) {
    return 'before';
  }
  if (ratio > 0.75) {
    return 'after';
  }
  return targetMenu.type === BASEMENUTYPEE.Dir ? 'child' : 'after';
};

const applyDropState = (rowEl: HTMLElement, mode: DropMode) => {
  clearDropState();
  rowEl.classList.add(`menu-drag-over-${mode}`);
};

const bindRows = () => {
  pageRef.value?.querySelectorAll<HTMLElement>('.ultra-table-row').forEach((rowEl) => {
    const titleEl = rowEl.querySelector<HTMLElement>('[data-menu-id]');
    if (!titleEl?.dataset.menuId) {
      return;
    }
    rowEl.dataset.menuId = titleEl.dataset.menuId;
    rowEl.draggable = true;
    rowEl.classList.add('menu-drag-row');
  });
};

const scheduleBindRows = () => {
  if (bindTimer) {
    window.clearTimeout(bindTimer);
  }
  void nextTick(() => {
    bindTimer = window.setTimeout(bindRows, 0);
  });
};

const onDragStart = (event: DragEvent) => {
  const rowEl = getDragRow(event.target);
  const menuId = getRowMenuId(rowEl);
  if (!rowEl || !menuId) {
    return;
  }
  draggingMenuId.value = menuId;
  rowEl.classList.add('menu-dragging');
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(menuId));
  }
};

const onDragOver = (event: DragEvent) => {
  const movingId = draggingMenuId.value || Number(event.dataTransfer?.getData('text/plain') || 0);
  const rowEl = getDragRow(event.target);
  const targetId = getRowMenuId(rowEl);
  const targetMenu = getMenuMap().get(targetId);
  if (!rowEl || !movingId || !targetId || movingId === targetId || !targetMenu) {
    return;
  }
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
  applyDropState(rowEl, getDropMode(rowEl, targetMenu, event));
};

const onDragLeave = (event: DragEvent) => {
  if (!pageRef.value?.contains(event.relatedTarget as Node | null)) {
    clearDropState();
  }
};

const moveMenu = async(payload: BusinessMenuMoveRequest) => {
  try {
    loading.value = true;
    await useApi().businessMenuApi.moveMenu(payload);
    ElMessage.success('移动成功');
    await tableRef.value?.refresh?.();
    scheduleBindRows();
  } finally {
    loading.value = false;
    draggingMenuId.value = undefined;
    clearDropState();
  }
};

const onDrop = (event: DragEvent) => {
  const movingId = draggingMenuId.value || Number(event.dataTransfer?.getData('text/plain') || 0);
  const rowEl = getDragRow(event.target);
  const targetId = getRowMenuId(rowEl);
  const targetMenu = getMenuMap().get(targetId);
  if (!rowEl || !movingId || !targetId || movingId === targetId || !targetMenu) {
    clearDropState();
    return;
  }
  event.preventDefault();
  void moveMenu({
    menuId: movingId,
    targetMenuId: targetId,
    mode: getDropMode(rowEl, targetMenu, event),
  });
};

const onDragEnd = () => {
  draggingMenuId.value = undefined;
  clearDropState();
};

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    const res = await useApi().businessMenuApi.listMenus({
      orderBys: filter.orderBys,
      conditions: cleanConditions(filter.conditions),
    });
    const data = toTree((res.data.data || []) as MenuRow[]);
    currentRows.value = flattenRows(data);
    refreshAddInitValues();
    scheduleBindRows();
    return {
      total: data.length,
      data,
    } as HpsfirProTableData;
  } finally {
    loading.value = false;
  }
});

const addFun = useBaseAsync(async(data: MenuRow) => {
  await useApi().businessMenuApi.addDirectory({
    title: data.title,
    sequence: data.sequence,
    icon: data.icon,
  });
  ElMessage.success('新增目录成功');
});

const formatDeleteInfo = (row: MenuRow) => `删除目录「${row.title}」后，该目录下的菜单会自动移动到同级目录，请确认操作`;

const deleteFun = useBaseAsync(async(data: MenuRow) => {
  if (data.type !== BASEMENUTYPEE.Dir) {
    ElMessage.warning('只能删除目录');
    return;
  }
  await useApi().businessMenuApi.deleteDirectory(data.id!);
  ElMessage.success('删除目录成功');
});

const updateFun = useBaseAsync(async(data: MenuRow) => {
  await useApi().businessMenuApi.updateMenu({
    id: data.id,
    title: data.title,
    sequence: data.sequence,
    icon: data.icon,
  });
  ElMessage.success('修改成功');
});

onMounted(() => {
  const pageEl = pageRef.value;
  if (!pageEl) {
    return;
  }
  pageEl.addEventListener('dragstart', onDragStart);
  pageEl.addEventListener('dragover', onDragOver);
  pageEl.addEventListener('dragleave', onDragLeave);
  pageEl.addEventListener('drop', onDrop);
  pageEl.addEventListener('dragend', onDragEnd);
  mutationObserver = new MutationObserver(bindRows);
  mutationObserver.observe(pageEl, { childList: true, subtree: true });
  scheduleBindRows();
});

onBeforeUnmount(() => {
  const pageEl = pageRef.value;
  pageEl?.removeEventListener('dragstart', onDragStart);
  pageEl?.removeEventListener('dragover', onDragOver);
  pageEl?.removeEventListener('dragleave', onDragLeave);
  pageEl?.removeEventListener('drop', onDrop);
  pageEl?.removeEventListener('dragend', onDragEnd);
  mutationObserver?.disconnect();
  if (bindTimer) {
    window.clearTimeout(bindTimer);
  }
});
</script>

<style lang="scss" scoped>
.menu-manage-page {
  :deep(.ultra-table-td:first-child .ultra-table-cell-content) {
    display: flex;
    align-items: center;
    white-space: nowrap;
  }

  :deep(.ultra-table-td:first-child .menu-title-text) {
    display: inline-flex;
    width: auto;
    min-width: 0;
  }

  :deep(.menu-drag-row) {
    cursor: grab;
  }

  :deep(.menu-drag-row:active) {
    cursor: grabbing;
  }

  :deep(.menu-dragging) {
    opacity: 0.55;
  }

  :deep(.menu-drag-over-before > td) {
    box-shadow: inset 0 2px 0 #409eff;
  }

  :deep(.menu-drag-over-after > td) {
    box-shadow: inset 0 -2px 0 #409eff;
  }

  :deep(.menu-drag-over-child > td) {
    background: rgba(64, 158, 255, 0.12);
    box-shadow: inset 0 0 0 2px rgba(64, 158, 255, 0.55);
  }
}
</style>
